import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import customAxios from 'routes/Pages/util/Api';
import authHeader from 'services/auth/jwt/auth-header';
import { useToast } from 'context/tostifyContext';
import useGetUser from 'routes/Auth/useGetUser';

const useContractNotification = () => {
  const { showToast } = useToast();

  const { user } = useGetUser();

  const crmListRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = event => {
      if (crmListRef.current && !crmListRef.current.contains(event.target)) {
        setShowCRMList(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const [selectedWH, setSelectedWH] = useState(null);
  const [shortMessage, setShortMessage] = useState('');
  const [users, setUsers] = useState([]);
  const [userMatrix, setUserMatrix] = useState([]);
  const [newUser, setNewUser] = useState('');

  // List
  const [showCRMList, setShowCRMList] = useState(false);
  const [crmUsers, setCrmUsers] = useState([]);
  const [crmSearch, setCrmSearch] = useState('');
  const [loadingCRM, setLoadingCRM] = useState(false);

  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [existingUsers, setExistingUsers] = useState([]);

  const [notifyNoRecipient, setNotifyNoRecipient] = useState(true);
  const [savingNoRecipient, setSavingNoRecipient] = useState(false);
  const [contractLoaded, setContractLoaded] = useState(false);
  const contractRef = useRef(null);

  const notificationOptions = [
    { value: 'DocketLoad', label: 'Docket Loading' },
    { value: 'TruckEntering', label: 'Truck Entering' },
    { value: 'DocketUnloading', label: 'Docket Unloading' },
  ];

  useEffect(() => {
    if (!selectedWH) {
      return;
    }

    setCrmUsers([]);
    setUsers([]);
    setUserMatrix([]);
    setContractLoaded(false);
    contractRef.current = null;

    const loadUsers = async () => {
      try {
        // Get CRM contacts for this WH
        const contractRes = await customAxios.get(`/Ops/GetContracts?contractUniqueID=${selectedWH}`, {
          headers: authHeader(),
        });

        const contract = contractRes?.data?.[0];
        contractRef.current = contract || null;
        setNotifyNoRecipient(contract?.notificationToSent ?? true);
        setContractLoaded(!!contract);

        const crmId = contract?.crmID;
        let crmUsers = [];

        try {
          const crmRes = await customAxios.get(`/CRM/GetContacts?id=1&crmId=${crmId}`, {
            headers: authHeader(),
          });
          crmUsers = (crmRes.data || []).map(c => ({
            id: `crm-${c.crmId || c.crmContactEmail || c.crmContactPhone}`,
            name: `${c.crmContactFirst || ''} ${c.crmContactLast || ''}`.trim() || c.crmContactEmail || c.crmContactPhone,
            email: c.crmContactEmail || '',
            phone: c.crmContactPhone || '',
            channels: { email: false, sms: false },
            notifications: Object.fromEntries(notificationOptions.map(o => [o.value, false])),
          }));
        } catch (error) {
          console.log(error);
        }

        // Get existing users to notify
        const existingRes = await customAxios.get(`/Ops/GetContractsUsersToNotify?contractUniqueID=${selectedWH}`, {
          headers: authHeader(),
        });

        const existing = existingRes.data || [];

        console.log('existingRes ====', existing, selectedWH);

        const normalize = v => (v || '').trim().toLowerCase();
        const existingMap = new Map();
        existing.forEach(e => {
          const key = normalize(e.emailOrPhone);
          if (!existingMap.has(key)) existingMap.set(key, []);
          existingMap.get(key).push(e);
        });

        // Merge existing info into CRM users
        const updatedCRM = crmUsers.map(u => {
          const keyEmail = normalize(u.email);
          const keyPhone = normalize(u.phone);
          const matches = [...(existingMap.get(keyEmail) || []), ...(existingMap.get(keyPhone) || [])];

          return {
            ...u,
            channels: {
              email: matches.some(m => m.notificationType === 'email'),
              sms: matches.some(m => m.notificationType === 'sms'),
            },
            notifications: Object.fromEntries(
              notificationOptions.map(o => [o.value, matches.some(m => m.notificationSection === o.value)]),
            ),
          };
        });

        // Add admin-only (extra) users
        const crmKeys = new Set(crmUsers.map(u => normalize(u.email || u.phone)));
        const extras = [];

        console.log('EXISTING MAP ======', existingMap);

        for (const [key, list] of existingMap) {
          if (crmKeys.has(key)) continue;
          const isEmail = key.includes('@');

          extras.push({
            id: `extra-${key}`,
            name: key,
            email: isEmail ? key : '',
            phone: isEmail ? '' : key,
            channels: {
              email: list.some(l => l.notificationType === 'email'),
              sms: list.some(l => l.notificationType === 'sms'),
            },
            notifications: Object.fromEntries(
              notificationOptions.map(o => [o.value, list.some(l => l.notificationSection === o.value)]),
            ),
          });
        }

        console.log('DATA =====>', updatedCRM, extras);

        // Final list
        const finalList = [...updatedCRM, ...extras];
        setUsers(finalList);
        setUserMatrix(finalList);
        setExistingUsers(finalList);
      } catch (err) {
        console.error('Error fetching users:', err);
        showToast('Failed to load users.');
        setUsers([]);
        setUserMatrix([]);
      }
    };

    loadUsers();
  }, [selectedWH]);

  useEffect(() => {
    if (!showCRMList) return;

    const fetchCRMUsers = async () => {
      try {
        setLoadingCRM(true);

        const res = await customAxios.get('/CRM/GetContacts?id=1', { headers: authHeader() });

        const data = res?.data
          ?.filter(item => item.crmContactEmail !== '' || item.crmContactPhone !== '')
          .map(item => {
            return {
              name: `${item.crmContactFirst} ${item.crmContactLast}`,
              email: item.crmContactEmail,
              phone: item.crmContactPhone,
            };
          });
        setCrmUsers(data || []);
      } catch (err) {
        console.error('Failed to fetch CRM users:', err);
        showToast('Failed to fetch CRM users.');
      } finally {
        setLoadingCRM(false);
      }
    };

    fetchCRMUsers();
  }, [showCRMList]);

  const toggleChannel = (userId, channel) => {
    setUserMatrix(prev =>
      prev.map(u => (u.id === userId ? { ...u, channels: { ...u.channels, [channel]: !u.channels[channel] } } : u)),
    );
  };

  const toggleNotification = (userId, section) => {
    setUserMatrix(prev =>
      prev.map(u =>
        u.id === userId
          ? {
            ...u,
            notifications: {
              ...u.notifications,
              [section]: !u.notifications[section],
            },
          }
          : u,
      ),
    );
  };

  const sendNotification = async () => {
    if (!selectedWH) {
      showToast('Please select a WH number.');
      return false;
    }

    try {
      const payload = [];

      userMatrix.forEach(u => {
        const activeSections = Object.keys(u.notifications).filter(k => u.notifications[k]);
        if (activeSections.length === 0) return;

        const activeChannels = Object.keys(u.channels).filter(ch => u.channels[ch]);
        if (activeChannels.length === 0) return;

        activeChannels.forEach(channel => {
          activeSections.forEach(section => {
            payload.push({
              contractUniqueID: selectedWH,
              userId: user?.id,
              notificationType: channel,
              emailOrPhone: channel === 'email' ? u.email : u.phone,
              notificationSection: section,
            });
          });
        });
      });

      // if (payload.length === 0) {
      //   showToast('No notifications selected.');
      //   return false;
      // }
      await customAxios.post(
        '/Ops/DeleteContractsUsersToNotify',
        {
          contractUniqueId: selectedWH,
        },
        { headers: authHeader() },
      );
      await Promise.allSettled(
        payload.map(p => customAxios.post('/Ops/AddContractsUsersToNotify', p, { headers: authHeader() })),
      );

      showToast('Notifications saved successfully!');
      return true;
    } catch (err) {
      console.error(err);
      showToast('Error saving notifications.');
      return false;
    }
  };

  const resetForm = () => {
    setSelectedWH(null);
    setShortMessage('');
    setNotifyNoRecipient(true);
    setContractLoaded(false);

    setUserMatrix(prev =>
      prev.map(u => ({
        ...u,
        channels: { email: false, sms: false },
        notifications: notificationOptions.reduce((acc, cur) => {
          acc[cur.value] = false;
          return acc;
        }, {}),
      })),
    );
  };

  // ADD

  const handleAddUser = () => {
    const value = newUser.trim();

    if (!value) return;

    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    const isPhone = /^[0-9+\-()\s]{6,20}$/.test(value);

    if (!isEmail && !isPhone) {
      showToast('Please enter a valid email or phone number.');
      return;
    }

    const identifier = value.toLowerCase();

    // Prevent duplicates
    const alreadyExists = userMatrix.some(
      u => (u.email && u.email.toLowerCase() === identifier) || (u.phone && u.phone === value),
    );

    if (alreadyExists) {
      showToast('This user already exists.');
      return;
    }

    const newUserEntry = {
      id: `new-${Date.now()}`,
      name: value,
      email: isEmail ? value : '',
      phone: isPhone ? value : '',
      channels: {
        email: isEmail,
        sms: isPhone,
      },
      notifications: notificationOptions.reduce((acc, cur) => {
        acc[cur.value] = false;
        return acc;
      }, {}),
    };

    setUserMatrix(prev => [...prev, newUserEntry]);
    setNewUser('');
  };

  const handleAddFromCRM = user => {
    const email = user.email?.trim()?.toLowerCase();
    const phone = user.phone?.trim();

    const exists = userMatrix.some(
      u => (email && u.email?.trim().toLowerCase() === email) || (phone && u.phone?.trim() === phone),
    );
    if (exists) {
      showToast('User already exists.');
      return;
    }

    const newEntry = {
      id: `crm-${user.id}`,
      name: user.name || user.email || user.phone,
      email: email || '',
      phone: phone || '',
      channels: {
        email: !!email,
        sms: !!phone,
      },
      notifications: notificationOptions.reduce((acc, cur) => {
        acc[cur.value] = false;
        return acc;
      }, {}),
    };

    setUserMatrix(prev => [...prev, newEntry]);
    showToast('User added from CRM.');
  };

  const handleToggleNoRecipient = async checked => {
    if (!contractRef.current || savingNoRecipient) return;

    const previous = notifyNoRecipient;
    setNotifyNoRecipient(checked); // optimistic
    setSavingNoRecipient(true);

    try {
      const updated = { ...contractRef.current, notificationToSent: checked };

      await customAxios.post(
        '/Ops/EditContract', // 👈 replace with your actual Edit Contract endpoint
        updated,
        { headers: authHeader() },
      );

      contractRef.current = updated;
      showToast(
        checked
          ? 'No Recipient notifications enabled for this contract.'
          : 'No Recipient notifications disabled for this contract.',
      );
    } catch (err) {
      console.error('Failed to update No Recipient setting:', err);
      setNotifyNoRecipient(previous); // rollback
      showToast('Failed to update setting.');
    } finally {
      setSavingNoRecipient(false);
    }
  };

  return {
    selectedWH,
    setSelectedWH,
    shortMessage,
    setShortMessage,
    users,
    userMatrix,
    notificationOptions,
    toggleChannel,
    toggleNotification,
    sendNotification,
    resetForm,
    newUser,
    setNewUser,
    handleAddUser,
    showCRMList,
    setShowCRMList,
    crmSearch,
    setCrmSearch,
    crmUsers,
    handleAddFromCRM,
    loadingCRM,
    crmListRef,
    existingUsers,
    setExistingUsers,
    showDiscardConfirm,
    setShowDiscardConfirm,

    notifyNoRecipient,
    handleToggleNoRecipient,
    savingNoRecipient,
    contractLoaded,
  };
};

export default useContractNotification;
